import {
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { useFormContext } from "react-hook-form";
import { Textarea } from "@/components/ui/textarea";

const preferenceTags = [
  "Cuisine française", "Cuisine italienne", "Cuisine asiatique",
  "Cuisine méditerranéenne", "Cuisine fusion", "Végétarien",
  "Vegan", "Fruits de mer", "Viande rouge",
];

const restrictionTags = [
  "Sans gluten", "Sans lactose", "Halal", "Casher",
  "Sans fruits à coque", "Sans crustacés", "Sans porc",
  "Sans œufs", "Sans soja",
];

function TagSelector({
  tags,
  value,
  onChange,
}: {
  tags: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  const selected = value
    ? value.split(",").map((s) => s.trim()).filter(Boolean)
    : [];

  const toggle = (tag: string) => {
    const next = selected.includes(tag)
      ? selected.filter((t) => t !== tag)
      : [...selected, tag];
    onChange(next.join(", "));
  };

  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag) => {
        const active = selected.includes(tag);
        return (
          <button
            key={tag}
            type="button"
            onClick={() => toggle(tag)}
            className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all duration-150 ${
              active
                ? "bg-primary text-white border-primary"
                : "bg-white border-border text-dark-blue hover:border-primary/40 hover:bg-primary/5"
            }`}
          >
            {tag}
          </button>
        );
      })}
    </div>
  );
}

export const Step6Preferences = () => {
  const form = useFormContext();

  return (
    <section className="space-y-8 w-full">
      <div className="flex flex-col gap-1 mb-2">
        <h1 className="font-h2 text-dark-blue">Vos goûts & restrictions</h1>
        <p className="font-p text-muted-foreground">
          Aidez-nous à trouver le chef idéal pour vous.
        </p>
      </div>

      {/* Preferences */}
      <FormField
        control={form.control}
        name="foodPreferences"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-dark-blue mb-0.5">Préférences culinaires</p>
              <p className="text-xs text-muted-foreground">Sélectionnez ou décrivez vos envies.</p>
            </div>
            <TagSelector
              tags={preferenceTags}
              value={field.value || ""}
              onChange={field.onChange}
            />
            <FormControl>
              <Textarea
                placeholder="Ou décrivez librement vos préférences..."
                className="resize-none bg-white rounded-xl border-2 focus:border-primary transition-colors min-h-[80px]"
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {/* Restrictions */}
      <FormField
        control={form.control}
        name="dietaryRestrictions"
        render={({ field }) => (
          <FormItem className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-dark-blue mb-0.5">Restrictions alimentaires</p>
              <p className="text-xs text-muted-foreground">Allergies ou régimes à respecter.</p>
            </div>
            <TagSelector
              tags={restrictionTags}
              value={field.value || ""}
              onChange={field.onChange}
            />
            <FormControl>
              <Textarea
                placeholder="Ou précisez vos restrictions alimentaires..."
                className="resize-none bg-white rounded-xl border-2 focus:border-primary transition-colors min-h-[80px]"
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                ref={field.ref}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </section>
  );
};
